import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { PencilIcon, DeleteIcon, RepeatAssessmentIcon } from '../../Global/Icons';
import { AnimatedProgress, FadeModal, OverlayLink, NewLinkBadge } from '../../Global/Transitions';
import Tests from '../../../Models/Tests';
import Project from '../../../Models/Project';
import EditProject from '../../Popups/EditProject';
import TestProps from '../../../Utilities/TestProps';
import { isAccessAllowed, UserRole } from '../../../Utilities/AccessControl';
import RepeatAssessment from '../../Popups/Projects/RepeatAssessment';
import { useUser } from '../../Global/RoleContext';



interface ProjectsRowProps {
  test: Tests;
  projects?: Project[];
  userrole?: UserRole;
  canOpen: boolean;
  projectId?: number | null;
  projectStatus?: string | null;
  OnProjectChange?: () => void;
}

const ProjectsRow: React.FC<ProjectsRowProps> = ({ test, projects, userrole, canOpen, projectId, projectStatus = null, OnProjectChange }) => {
  const repeatAssessmentAllowedRoles = ['Super User', 'Project Manager'];
  const { myApplicationRole, highestPrivilegeRole } = useUser();
  const props = TestProps[test.status];
  const [show, setShow] = useState(false);
  const handleHide = () => setShow(false);
  const [action, setAction] = useState(null);
  const isCreated = test.status === 'Created';
  const [complete, setComplete] = useState(false);
  const [showRepeatAssessment, setShowRepeatAssessment] = useState(false);
  const handleComplete = () => { setComplete(true); };
  const canAccess = isAccessAllowed(userrole);
  const matchingRole = myApplicationRole?.find((role) => role?.id === test.ticketno);
  const hasRepeatAssessmentRole =
    (!!highestPrivilegeRole && repeatAssessmentAllowedRoles.includes(highestPrivilegeRole)) ||
    (!!matchingRole && repeatAssessmentAllowedRoles.includes(matchingRole.role));
  const isRepeatAssessmentStatusEligible = !!projectStatus && ['Post Project', 'Done'].includes(projectStatus);
  const canCreateRepeatAssessment = hasRepeatAssessmentRole && isRepeatAssessmentStatusEligible;
  const repeatAssessmentDisabledReason = !projectStatus
    ? 'Project details are unavailable. Please refresh or check access.'
    : 'Repeat Assessment is only available when status is Report Available or Done.';
  const baseLink = '/projects/' + test.id + '/' + (isCreated ? 'setup' : (projectId || ''));
  const extraLink = '/projects/' + test.id + '/' + (isCreated ? '' : (projectId || ''));
  const link = canAccess ? baseLink : extraLink;



  const handleShow = (action) => { setComplete(false); setAction(action); setShow(true) };


 
  const handleLinkClick = (e: React.MouseEvent) => {
  if (!canOpen) {
    e.preventDefault();
    e.stopPropagation();
  }
};

  return (
    <tr key={test.id}>
      <td><Link to={canOpen ? link : '#'}
    onClick={handleLinkClick}
    className={`stretched-link ${!canOpen ? 'disabled-link' : ''}`}>{test.projectName}</Link></td>
       <td><NewLinkBadge to={canOpen ? link : '#'}
    bullet
    status={test.status}
    onClick={handleLinkClick}>{test.status}</NewLinkBadge></td>
      <td>{test.projectOwner}</td>
      <td>{formatDistanceToNow(+test._ts * 1000)} ago</td>
      <td width={70}><AnimatedProgress initialProgress={props.progress} /></td>
      <td width={40}>
        <OverlayLink
          body={<RepeatAssessmentIcon  width={18} style={{ marginTop: '-3' }} />}
          disabled={!canCreateRepeatAssessment}
          onClick={() => setShowRepeatAssessment(true)}
          placement='left'
          message={repeatAssessmentDisabledReason}
        />
      </td>
      <td width={40}><OverlayLink body={<PencilIcon />} disabled={!isCreated || !canAccess} onClick={() => handleShow('edit')} placement='left' /></td>
      <td width={60}><OverlayLink body={<DeleteIcon />} disabled={!isCreated || !canAccess} onClick={() => handleShow('delete')} className='text-danger' /></td>
      <FadeModal show={show} onHide={handleHide} title={complete ? '' : (action === 'edit' ? 'Edit' : 'Delete') + ' Project'}>
        <EditProject action={action} test={test} close={handleHide} complete={handleComplete} />
      </FadeModal>
      <FadeModal show={showRepeatAssessment} onHide={() => setShowRepeatAssessment(false)} title='Repeat Assessment'>
        <RepeatAssessment
          test={test}
          close={() => {
            setShowRepeatAssessment(false);
            OnProjectChange?.();
          }}
          complete={() => {
            setShowRepeatAssessment(false);
            OnProjectChange?.();
          }}
        />
      </FadeModal>
    </tr>
  );
};


export default ProjectsRow;